import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Restore scroll position when user navigates BACK to a list page.
 *
 * Approach:
 *  - Listen to scroll events; track the latest scrollY in a ref + persist it
 *    (throttled) to sessionStorage keyed by pathname+search.
 *  - On mount with `navigationType === "POP"` (browser back/forward), read the
 *    saved value and restore it. Retries on a tick loop while content paints.
 *  - Disable the browser's default scroll restoration so our restore wins.
 *
 * Mount once at the top of any list/index page that should preserve scroll.
 */
export function useScrollRestore(deps = []) {
  const location = useLocation();
  const navType = useNavigationType();
  const savedRef = useRef(null);
  const keyRef = useRef(`scroll:${location.pathname}${location.search}`);
  const lastYRef = useRef(typeof window !== "undefined" ? window.scrollY : 0);

  useEffect(() => {
    keyRef.current = `scroll:${location.pathname}${location.search}`;
  }, [location.pathname, location.search]);

  // Disable browser auto-restore so manual logic wins
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      const prev = window.history.scrollRestoration;
      window.history.scrollRestoration = "manual";
      return () => {
        window.history.scrollRestoration = prev;
      };
    }
  }, []);

  // Track scroll, throttle-save to sessionStorage
  useEffect(() => {
    let throttleId = null;
    const persist = () => {
      try {
        if (lastYRef.current > 0) {
          sessionStorage.setItem(keyRef.current, String(lastYRef.current));
        }
      } catch {
        // ignore
      }
    };
    const onScroll = () => {
      lastYRef.current = window.scrollY;
      if (throttleId) return;
      throttleId = setTimeout(() => {
        throttleId = null;
        persist();
      }, 120);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (throttleId) clearTimeout(throttleId);
      // On unmount, persist the LAST KNOWN scroll value (lastYRef), not whatever
      // window.scrollY happens to be right now (which may already be 0 because
      // React Router started rendering the new route).
      try {
        if (lastYRef.current > 0) {
          sessionStorage.setItem(keyRef.current, String(lastYRef.current));
        }
      } catch {
        // ignore
      }
    };
  }, []);

  // On POP, read saved value
  useEffect(() => {
    if (navType !== "POP") {
      savedRef.current = null;
      return;
    }
    try {
      const raw = sessionStorage.getItem(keyRef.current);
      if (raw !== null) {
        savedRef.current = Number(raw) || 0;
      }
    } catch {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navType]);

  // Restore once content paints; retry while content height grows
  useEffect(() => {
    if (savedRef.current === null || savedRef.current === 0) return;
    const target = savedRef.current;
    let attempts = 0;
    const tick = () => {
      window.scrollTo(0, target);
      attempts += 1;
      const reached = Math.abs(window.scrollY - target) <= 4;
      if (reached || attempts >= 60) {
        savedRef.current = null;
        return;
      }
      setTimeout(tick, 80);
    };
    const id = setTimeout(tick, 80);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navType, ...deps]);
}

export default useScrollRestore;
